import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsAPI } from '@/utils/api';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Heart, Star, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const { isInWishlist, toggleWishlist } = useCart();
  const { addToCart } = useCart();
  const [activeImage, setActiveImage] = useState('');

  const loadProduct = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productsAPI.getById(id);
      setProduct(data);
      
      // Defaults
      if (data.sizes?.length > 0) setSelectedSize(data.sizes[0]);
      
      if (data.colors?.length > 0) {
          const defaultColor = data.colors[0];
          setSelectedColor(defaultColor);
          
          // STRICT FIX: Look for the image in the map. If not found, use index 0.
          const mappedImage = data.colorImages?.[defaultColor];
          setActiveImage(mappedImage || data.images?.[0]);
      } else {
          setActiveImage(data.images?.[0]);
      }
      
    } catch (error) {
      console.error(error);
      navigate('/products');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (product && selectedColor && product.colorImages) {
        const specificImage = product.colorImages[selectedColor];
        
        // If we found a specific image for this color, update the view
        if (specificImage) {
            setActiveImage(specificImage);
        } else {
            // Fallback: If for some reason the map is missing this color, 
            // do NOT change the image (or revert to main).
            // Usually, we want to reset to main if specific mapping is lost.
            // setActiveImage(product.images[0]); 
        }
    }
  }, [selectedColor, product]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add to cart');
      navigate('/login');
      return;
    }

    if (product.sizes?.length > 0 && !selectedSize) {
      toast.error('Please select a size');
      return;
    }
    if (product.colors?.length > 0 && !selectedColor) {
      toast.error('Please select a color');
      return;
    }
    try {
      await addToCart(product, selectedSize, selectedColor, quantity);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (!product) return null;

  const inWishlist = isInWishlist(product.id);

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to add to wishlist');
      return;
    }
    try {
      // We pass the whole product to Context (so it can update UI instantly)
      await toggleWishlist(product);
      toast.success(inWishlist ? 'Removed from wishlist' : 'Added to wishlist');
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  // --- HELPER: Determine Max Stock ---
  // If stock is a number, use it. If boolean (true), allow up to 10 items.
  const getMaxStock = () => {
    if (!product) return 1;
    if (typeof product.stock === 'number') return product.stock;
    return product.stock ? 10 : 0; // Default limit for "In Stock" items
  };

  const handleIncreaseQty = () => {
    const max = getMaxStock();
    setQuantity((prev) => Math.min(max, prev + 1));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!product) return null;

  // --- FIX: Safely calculate price (Parse String to Float) ---
  const rawPrice = parseFloat(product.price || 0);
  const discount = parseFloat(product.discount || 0);
  const discountedPrice = rawPrice * (1 - discount / 100);

  return (
    <div className="min-h-screen py-8" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-[3/4] overflow-hidden rounded-xl bg-gray-100 relative group">
              <img
                src={activeImage || "https://placehold.co/600?text=No+Image"} // Use activeImage state
                alt={product.name}
                className="w-full h-full object-cover transition-all duration-500" // Added smooth transition
                data-testid="product-main-image"
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
                {product.images?.map((img, idx) => (
                    <button 
                        key={idx}
                        onClick={() => setActiveImage(img)} // Manual override
                        className={`aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                            activeImage === img ? 'border-black ring-1 ring-black' : 'border-transparent hover:border-gray-300'
                        }`}
                    >
                        <img src={img} alt={`View ${idx}`} className="w-full h-full object-cover" />
                    </button>
                ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6" data-testid="product-info">
            <div>
              <h1 className="font-outfit font-bold text-3xl md:text-4xl mb-2" data-testid="product-name">
                {product.name}
              </h1>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 fill-primary text-primary" />
                  <span className="font-medium">{product.rating || 4.5}</span>
                </div>
                <span className="text-muted-foreground text-sm">
                  {/* FIX: Handle boolean stock status display */}
                  {typeof product.stock === 'number'
                    ? `(${product.stock} in stock)`
                    : (product.stock ? 'In Stock' : 'Out of Stock')}
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="font-mono font-bold text-4xl" data-testid="product-price">
                ₹{discountedPrice.toFixed(2)}
              </span>
              {discount > 0 && (
                <>
                  <span className="text-muted-foreground text-xl line-through">
                    ₹{rawPrice.toFixed(2)}
                  </span>
                  <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-sm font-bold">
                    {discount}% OFF
                  </span>
                </>
              )}
            </div>

            {/* Description (Strip HTML tags if any) */}
            <div
              className="text-muted-foreground prose prose-sm max-w-none"
              data-testid="product-description"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
            {/* Size Selection - Radio Buttons */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-bold text-gray-900">Select Size</label>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`
                        min-w-[3rem] h-12 px-3 rounded-lg flex items-center justify-center border text-sm font-bold transition-all
                        ${selectedSize === size 
                          ? 'border-black bg-black text-white shadow-md transform scale-105' 
                          : 'border-gray-200 text-gray-700 bg-white hover:border-black'
                        }
                      `}
                      data-testid={`size-option-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection - Visual Circles */}
            {product.colors && product.colors.length > 0 && (
              <div className="space-y-3">
                <label className="text-sm font-bold text-gray-900">
                  Select Color: <span className="font-medium text-gray-500 capitalize">{selectedColor}</span>
                </label>
                
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color) => {
                    // 1. Define custom mappings for non-standard CSS colors
                    const colorMap = {
                      'navy blue': '#121a26',
                      'bottle green': '#006A4E',
                      'baby blue': '#a1c0d4',
                      'jade': '#8aa789',
                      'light baby pink': '#F4C2C2',
                      'baby pink': '#F4C2C2',
                      'petrol blue': '#1e373b',
                      'flamingo': '#FC8EAC',
                      'maroon': '#800000',
                      'burgundy': '#800020',
                      'mustard': '#FFDB58',
                      'off white': '#FAF9F6',
                      'charcoal': '#36454F',
                      'khaki': '#F0E68C',
                      'mauve': '#E0B0FF',
                      'peach': '#FFE5B4'
                    };

                    // 2. Normalize input: lowercase, remove special chars if needed
                    const normalizedColor = color.toLowerCase().trim();

                    // 3. Resolve final background color
                    // Priority: Mapped Hex -> Standard CSS Name -> Raw Value
                    const bgStyle = colorMap[normalizedColor] || normalizedColor.replace(' ', '');

                    return (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`
                          w-10 h-10 rounded-full flex items-center justify-center transition-all relative border shadow-sm
                          ${selectedColor === color 
                            ? 'border-black ring-2 ring-offset-2 ring-gray-300 scale-110' 
                            : 'border-gray-200 hover:scale-105 hover:border-gray-400'
                          }
                        `}
                        title={color}
                        data-testid={`color-option-${color}`}
                        style={{ backgroundColor: bgStyle }} 
                      >
                        {/* Selected Indicator (Dot) */}
                        {selectedColor === color && (
                          <div className={`w-1.5 h-1.5 rounded-full shadow-sm ${
                            ['white', 'yellow', 'cream', 'beige', 'off white', 'baby blue', 'mustard'].includes(normalizedColor) 
                              ? 'bg-black' 
                              : 'bg-white'
                          }`} />
                        )}
                        
                        {/* Fallback border for white/light colors so they don't disappear against white background */}
                        {(normalizedColor === 'white' || normalizedColor === 'off white') && (
                          <div className="absolute inset-0 rounded-full border border-gray-300 opacity-30"></div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="space-y-2">
              <label className="text-sm font-semibold">Quantity</label>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="decrease-quantity-button"
                  disabled={quantity <= 1}
                >
                  -
                </Button>
                <span className="font-medium w-12 text-center" data-testid="quantity-display">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  // --- FIX: Use updated handler ---
                  onClick={handleIncreaseQty}
                  data-testid="increase-quantity-button"
                  // Disable if we hit the limit
                  disabled={quantity >= getMaxStock()}
                >
                  +
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                onClick={handleAddToCart}
                className="flex-1 rounded-full py-6 bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                data-testid="add-to-cart-button"
                // Disable if out of stock
                disabled={!product.stock}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {product.stock ? 'Add to Cart' : 'Out of Stock'}
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="rounded-full w-12 h-12"
                onClick={handleToggleWishlist}
                data-testid="wishlist-toggle-button"
              >
                <Heart
                  className={`w-6 h-6 ${isInWishlist(product.id) ? 'fill-red-500 text-red-500' : ''}`}
                />
              </Button>
            </div>

            {/* Product Details Footer */}
            <div className="border-t border-border pt-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Category</span>
                <span className="font-medium">{product.category || "General"}</span>
              </div>
              {product.gender && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Gender</span>
                  <span className="font-medium capitalize">{product.gender}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};